import React from 'react';
import {
  HomeIcon,
  UserIcon,
  KeyRoundIcon,
  ShoppingCartIcon,
  PlaneTakeoffIcon,
  Sparkles,
} from 'lucide-react';
import {NavLinks} from './useNavigationLinks';
import {PATHS} from 'routes/constants';
import {useSummary} from 'context/summary';
import {useSchema} from 'context/guest';
import {usePaymentsCart} from './payments';
import {usePath} from './common';
import {useLocation, useMatch, useNavigate} from 'react-router-dom';
import {useNavigationCustom} from './useNavigationCustom';
import {useFetchLockConditions} from './remoteAccess';
import {useGuestGroup} from './useGuestGroup';
import {useBuildNavigationItems} from './useBuildNavigationItems';
import {useCheckoutSettings} from 'pages/Checkout/hooks/useCheckoutSettings';
import {usePostHogTrack, POSTHOG_EVENTS} from 'analytics';
import {useIVOnProperty} from './useIVOnProperty';

type SideBarLinks = {
  [key: string]: {
    label: NavLinks;
    selected: boolean;
    disabled: boolean;
    icon: JSX.Element;
    onClick: () => void;
    extraData?: string;
  };
};

function useSideBarLinks() {
  const {arePaymentsAvailable, data} = useSummary();
  const navigate = useNavigate();
  const {pathname} = useLocation();
  const {getFullPath, getMainPath} = usePath();
  const {currentPage} = useNavigationCustom();
  const {trackEvent} = usePostHogTrack();
  const {isCheckoutEnabled, isAvailable} = useCheckoutSettings();
  const {data: locksData} = useFetchLockConditions({
    enabled: Boolean(data?.is_remote_access_enabled),
  });
  const {
    identityVerificationDetails: {enabled: ivEnabled},
  } = useSchema();
  const {guestGroup} = useGuestGroup();
  const {getPaymentPath} = useBuildNavigationItems();
  const isRemoteAccessEnabled =
    data?.is_remote_access_enabled &&
    locksData?.is_show_keys_enabled &&
    locksData?.is_feature_active;
  const {paymentsCart} = usePaymentsCart({refetchOnMount: false});
  const isHomeView = useMatch(getMainPath(PATHS.home));
  const isPaymentsView = pathname.includes(PATHS.payments.main);
  const isRemoteAccessView = useMatch(getMainPath(PATHS.remoteAccess));
  const isCheckOutView = useMatch(getMainPath(PATHS.checkout.main));
  const isRecommendationsView = pathname.includes(PATHS.recommendations.main);
  const hasNoGuests = guestGroup?.added_number_of_guests === 0;
  const {isIVOnPropertyUnavailable} = useIVOnProperty();

  const getRegistrationPath = React.useCallback(() => {
    if (isIVOnPropertyUnavailable) return PATHS.guestRegistration;
    if (ivEnabled) {
      if (hasNoGuests) return PATHS.verification.main;
      return PATHS.guestRegistration;
    }
    if (hasNoGuests) return PATHS.addPersonalDataForm;
    return PATHS.guestRegistration;
  }, [isIVOnPropertyUnavailable, ivEnabled, hasNoGuests]);

  const mainLinks = React.useMemo(() => {
    const links: SideBarLinks = {
      [NavLinks.home]: {
        label: NavLinks.home,
        selected: Boolean(isHomeView),
        disabled: false,
        icon: <HomeIcon />,
        onClick: () => navigate(getFullPath(PATHS.home)),
      },
      [NavLinks.onlineChekin]: {
        label: NavLinks.onlineChekin,
        selected:
          !isHomeView &&
          !isPaymentsView &&
          !isRemoteAccessView &&
          !isCheckOutView &&
          !isRecommendationsView,
        disabled: false,
        icon: <UserIcon />,
        onClick: () => navigate(getFullPath(getRegistrationPath())),
      },
      // [NavLinks.homebook]: {
      //   label: NavLinks.homebook,
      //   selected: false,
      //   disabled: false,
      //   iconName: 'locationSidebar',
      //   path: 'adsfasfas',
      // },
    };
    if (isRemoteAccessEnabled) {
      links[NavLinks.virtualKeys] = {
        label: NavLinks.virtualKeys,
        selected: Boolean(isRemoteAccessView),
        disabled: false,
        icon: <KeyRoundIcon />,
        onClick: () => navigate(getFullPath(PATHS.remoteAccess)),
      };
    }
    if (arePaymentsAvailable && paymentsCart?.pay_now_payments) {
      links[NavLinks.paymentCart] = {
        label: NavLinks.paymentCart,
        selected: isPaymentsView,
        disabled: false,
        icon: <ShoppingCartIcon />,
        onClick: () =>
          navigate(getFullPath(getPaymentPath()), {state: {from: currentPage}}),
      };

      if (paymentsCart?.pay_now_payments?.length) {
        links[NavLinks.paymentCart]['extraData'] =
          paymentsCart?.pay_now_payments?.length.toString();
      }
    }
    if (isCheckoutEnabled && isAvailable) {
      links[NavLinks.checkOut] = {
        label: NavLinks.checkOut,
        selected: Boolean(isCheckOutView),
        disabled: false,
        icon: <PlaneTakeoffIcon />,
        onClick: () => navigate(getFullPath(PATHS.checkout.main)),
      };
    }
    if (data?.upselling?.active) {
      links[NavLinks.enhanceYourStay] = {
        label: NavLinks.enhanceYourStay,
        selected: isRecommendationsView,
        disabled: false,
        icon: <Sparkles />,
        onClick: () => {
          trackEvent(POSTHOG_EVENTS.RECOMMENDATIONS_CLICKED);
          navigate(getFullPath(PATHS.recommendations.main));
        },
      };
    }
    return links;
  }, [
    isHomeView,
    isPaymentsView,
    isRemoteAccessView,
    isRemoteAccessEnabled,
    arePaymentsAvailable,
    paymentsCart?.pay_now_payments,
    isCheckOutView,
    isCheckoutEnabled,
    isAvailable,
    isRecommendationsView,
    data?.upselling?.active,
    navigate,
    getFullPath,
    currentPage,
    getRegistrationPath,
    getPaymentPath,
    trackEvent,
  ]);

  const navItems = React.useMemo(() => {
    return Object.values(mainLinks);
  }, [mainLinks]);

  return {navItems};
}

export {useSideBarLinks};
